import MediaManager from "@/components/media-manager/MediaManager";
import { useNode } from "@craftjs/core";
import { FormControl, FormControlLabel, FormGroup, InputLabel, MenuItem, Select, Stack, Switch, TextField, ToggleButton } from "@mui/material";
import { ImageProps } from "./ImageSettings/ImageSettings";
import useResponsiveValues, { ResponsiveValue } from "../hooks/useResponsiveValues";
import SettingCard from "@/components/SettingCard";
import DimentionSettings from "./DimentionSettings/DimentionSettings";
import ClassSettings from "./ClassSettings";
import MediaContent from "@/components/MediaContent";
import BorderRadiusSettings from "./BorderRadius/BorderRadiusSettings";
import SortableList from "@/components/SortableList";
import { v4 } from "uuid";

enum ObjectFit {
  NONE = "none",
  FILL = "fill",
  CONTAIN = "contain",
  COVER = "cover",
  SCALE_DOWN = "scale-down",
}

enum ObjectPosition {
  TOP = "top",
  BOTTOM = "bottom",
  LEFT = "left",
  RIGHT = "right",
  CENTER = "center",
}
export interface Slide {
  id: string;
  label: string;
  src: string;
}

export interface CarouselProps {
  slides: ResponsiveValue<Slide[]>;
  navigation?: boolean; // Add navigation prop
  pagination?: boolean; // Add pagination prop
  autoplay?: boolean | { delay: number; disableOnInteraction: boolean }; // Add autoplay prop
}

const ImagesSource = () => {
  const {
    props,
    actions: { setProp },
  } = useNode((node) => ({
    props: node.data.props,
  }));
  console.log("🚀 ~ ImagesSource ~ props:", props);

  const responsive = useResponsiveValues();

  return (
    <Stack spacing={2}>
      <Stack spacing={2} direction="row">
        <Stack flex={1} spacing={2}>
          <SortableList<Slide[]>
            items={responsive.getValue(props.slides) ?? []}
            onChange={(slides: Slide[]) => {
              setProp((props: CarouselProps) => {
                props.slides = responsive.setValue(props.slides, slides) as Partial<Record<string, Slide[]>>;
              });
            }}
            onDelete={(slides: Slide[]) => {
              setProp((props: CarouselProps) => {
                props.slides = responsive.setValue(props.slides, slides) as Partial<Record<string, Slide[]>>;
              });
            }}
            renderItem={(slide) => {
              return <MediaContent title={responsive.getValue(slide.label)} src={responsive.getValue(slide.src)} />;
            }}
          />
          <MediaManager
            multiple
            onChange={(images: string[]) => {
              setProp((props: CarouselProps) => {
                const oldSlides = responsive.getValue(props.slides) || [];

                const newSlides: Slide[] = images.map((image) => {
                  const id = v4();

                  return {
                    id: id,
                    label: "Imagen - " + id,
                    src: image,
                  };
                });

                props.slides = responsive.setValue(props.slides, [...oldSlides, ...newSlides]) as Partial<Record<string, Slide[]>>;
              });
            }}
          />
          {/* Navigation, Pagination, Autoplay Switches */}
          <FormControl component="fieldset" variant="standard">
            <FormGroup row>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={!!responsive.getValue(props.navigation)}
                    onChange={(_, value) => {
                      setProp((props: CarouselProps) => {
                        props.navigation = value;
                        return props;
                      });
                    }}
                  />
                }
                label="Navegación"
                labelPlacement="start"
              />
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={!!responsive.getValue(props.pagination)}
                    onChange={(_, value) => {
                      setProp((props: CarouselProps) => {
                        props.pagination = value;
                        return props;
                      });
                    }}
                  />
                }
                label="Paginación"
                labelPlacement="start"
              />
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={!!responsive.getValue(props.autoplay)}
                    onChange={(_, value) => {
                      setProp((props: CarouselProps) => {
                        props.autoplay = value;
                        return props;
                      });
                    }}
                  />
                }
                label="Autoplay"
                labelPlacement="start"
              />
            </FormGroup>
          </FormControl>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={2}>
        <FormControl fullWidth size="small">
          <InputLabel>Posición</InputLabel>
          <Select
            label="Posición"
            value={responsive.getValue(props.objectPosition)}
            onChange={(e) => {
              setProp((props: ImageProps) => {
                props.objectPosition = responsive.setValue(props.objectPosition, e.target.value);
                return props;
              });
            }}
          >
            <MenuItem value={ObjectPosition.TOP}>Superior</MenuItem>
            <MenuItem value={ObjectPosition.BOTTOM}>Inferior</MenuItem>
            <MenuItem value={ObjectPosition.LEFT}>Izquierda</MenuItem>
            <MenuItem value={ObjectPosition.RIGHT}>Derecha</MenuItem>
            <MenuItem value={ObjectPosition.CENTER}>Centro</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel>Ajuste</InputLabel>
          <Select
            label="Ajuste"
            value={responsive.getValue(props.objectFit)}
            onChange={(e) => {
              setProp((props: ImageProps) => {
                props.objectFit = responsive.setValue(props.objectFit, e.target.value);
                return props;
              });
            }}
            defaultValue=""
          >
            <MenuItem value="">Ninguno</MenuItem>
            <MenuItem value={ObjectFit.FILL}>Rellenar</MenuItem>
            <MenuItem value={ObjectFit.CONTAIN}>Contener</MenuItem>
            <MenuItem value={ObjectFit.COVER}>Cubrir</MenuItem>
            <MenuItem value={ObjectFit.SCALE_DOWN}>Escalar hacia abajo</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );
};

const CarouselSettings = () => {
  return (
    <Stack spacing={2}>
      <SettingCard title="Clases CSS">
        <ClassSettings />
      </SettingCard>

      <SettingCard title="Source">
        <ImagesSource />
      </SettingCard>

      <SettingCard title="Bordes Redondeados">
        <BorderRadiusSettings />
      </SettingCard>

      <SettingCard title="Dimensiones">
        <DimentionSettings />
      </SettingCard>
    </Stack>
  );
};

export default CarouselSettings;
